import React, { useState, useEffect } from "react";
import {
  Button,
  Grid,
  TextField,
  Typography,
  Box,
  useTheme,
} from "@mui/material";
import JerseyIcon from "../JerseyIcon";
import { GainLossSlider } from "../GainLossSlider";
import { DepthChartPlayer } from "../../../../types/DepthChartTypes";
import { useDepthChart } from "../../../../contexts/DepthChartContext";
import { useLogger } from "../../../../contexts/LoggerContext";

interface Props {
  clearSelections: boolean;
  setClearSelections: (clearSelections: boolean) => void;
}

export const OffenseRunLog = ({ clearSelections, setClearSelections }: Props) => {
  const [gainLoss, setGainLoss] = useState(0);
  const [selectedBallCarrier, setSelectedBallCarrier] =
    useState<DepthChartPlayer | null>(null);
  const [jerseyNumber, setJerseyNumber] = useState<string>("");
  const { ballCarriers } = useDepthChart();
  const { setCurrentPlayLog, setTouchdown, setSafety } = useLogger();
  const theme = useTheme();


  const handleJerseyNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    if (value === "" || (parseInt(value) >= 0 && parseInt(value) <= 99)) {
      setJerseyNumber(value);
    }
  };

  const handleBallCarrierSelection = (player: DepthChartPlayer) => {
    setSelectedBallCarrier(player);
    setJerseyNumber(player.jersey_number?.toString() || "");
  };

  useEffect(() => {
    if (selectedBallCarrier) {
      setCurrentPlayLog({
        rusher: selectedBallCarrier,
        gain_loss: gainLoss,
      });
    }
  }, [selectedBallCarrier, gainLoss]);

  useEffect(() => {

    if (clearSelections) {
      setSelectedBallCarrier(null);
      setJerseyNumber("");
      setGainLoss(0);
      setClearSelections(false);
    }
  }, [clearSelections]);

  return (
    <Box>
      <Grid container>
        {/* Ball Carrier */}
        <Grid item xs={4}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography>Ball Carrier</Typography>
            <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
              {ballCarriers.map((player: DepthChartPlayer) => (
                <JerseyIcon
                  key={player.id}
                  number={player.jersey_number}
                  name={player.name}
                  onClick={() => handleBallCarrierSelection(player)}
                  selected={selectedBallCarrier?.id === player.id}
                />
              ))}
            </Box>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <TextField
                type="number"
                label="Jersey Number"
                value={jerseyNumber}
                fullWidth
                onChange={handleJerseyNumberChange}
                inputProps={{
                  min: 0,
                  max: 99,
                  step: 1,
                }}
              />
            </Box>
          </Box>
        </Grid>
        {/* Gain/Loss */}
        <Grid item xs={6}>
          <GainLossSlider
            value={gainLoss}
            onChange={setGainLoss}
            label="Gain/Loss"
          />
        </Grid>
        {/* Special Situations */}
        <Grid item xs={2}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Button 
              variant="contained" 
              sx={{ 
                backgroundColor: theme.palette.button.yellow.background,
                color: theme.palette.button.yellow.text,
                '&:hover': {
                  backgroundColor: theme.palette.button.yellow.background,
                }
              }}
            >
              <Typography>Penalty</Typography>
            </Button>
            <Button 
              variant="contained" 
              sx={{ 
                backgroundColor: theme.palette.button.red.background,
                color: theme.palette.button.red.text,
                '&:hover': {
                  backgroundColor: theme.palette.button.red.background,
                }
              }}
            >
              <Typography>Fumble</Typography>
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
